import { type SxProps, type Theme } from '@mui/material'
import type ThemeInterface from '@/theme-interface'

/**
 * Theme-based styles for placeholder components
 * Replaces the CSS file with emotion/sx based styling
 */

/**
 * Base placeholder styles for text placeholders
 */
export const getPlaceholderStyles = (theme: ThemeInterface): SxProps<Theme> => ({
  '& .ProseMirror .placeholder': {
    display: 'inline',
    cursor: 'pointer',
    border: `1px dashed ${theme.primary}`,
    padding: '2px 4px',
    borderRadius: theme.borderRadius,
    backgroundColor: `${theme.primary}15`,
    color: theme.primary,
    fontWeight: 'normal',
    userSelect: 'none',
    position: 'relative',
    transition: 'all 0.2s ease-in-out',

    '&:hover': {
      backgroundColor: `${theme.primary}25`,
    },

    '&.ProseMirror-selectednode': {
      backgroundColor: `${theme.primary}35`,
      borderColor: theme.primaryDark,
      outline: `2px solid ${theme.primary}`,
      outlineOffset: '1px',
    },
  },

  // HTML placeholders in content (not in NodeView)
  '& .ProseMirror .placeholder-html': {
    display: 'inline',
    cursor: 'pointer',
    border: `1px dashed ${theme.borderColor}`,
    padding: '2px 4px',
    borderRadius: theme.borderRadius,
    backgroundColor: theme.backgroundColor,
    transition: 'all 0.2s ease-in-out',

    '&:hover': {
      backgroundColor: theme.backgroundColorLight,
      borderColor: theme.primary,
    },

    '&.ProseMirror-selectednode': {
      backgroundColor: `${theme.primary}15`,
      borderColor: theme.primary,
    },
  },
})

/**
 * Styles for HTML placeholders
 */
export const getHtmlPlaceholderStyles = (theme: ThemeInterface): SxProps<Theme> => ({
  '& .ProseMirror .placeholder-html': {
    display: 'inline',
    cursor: 'pointer',
    border: `1px dashed ${theme.borderColor}`,
    padding: '2px 4px',
    borderRadius: theme.borderRadius,
    backgroundColor: theme.backgroundColor,
    userSelect: 'none',
    position: 'relative',
    transition: 'all 0.2s ease-in-out',

    '&:hover': {
      backgroundColor: theme.backgroundColorLight,
      borderColor: theme.text,
    },

    '&.ProseMirror-selectednode': {
      backgroundColor: theme.backgroundColorBeige,
      borderColor: theme.text,
      outline: `2px solid ${theme.borderColor}`,
      outlineOffset: '1px',
    },
  },
})

/**
 * Combined placeholder styles for the editor
 */
export const getPlaceholderEditorStyles = (theme: ThemeInterface): SxProps<Theme> => ({
  '.ProseMirror .node-placeholder': {
    display: 'inline-block',
    width: 'fit-content',
  },
  // Unified styles for both text and HTML placeholders
  '& .ProseMirror .placeholder, & .ProseMirror .placeholder-html': {
    display: 'inline',
    cursor: 'pointer',
    border: `1px dashed ${theme.primary}`,
    padding: '2px 4px',
    borderRadius: theme.borderRadius,
    backgroundColor: `${theme.primary}15`,
    color: theme.primary,
    fontWeight: 'normal',
    userSelect: 'none',
    position: 'relative',
    transition: 'all 0.2s ease-in-out',

    '&:hover': {
      backgroundColor: `${theme.primary}25`,
      borderColor: theme.primaryDark,
    },

    // Prevent default actions on links, buttons, etc. in HTML placeholders
    '& a, & button, & [href], & [onclick]': {
      pointerEvents: 'none',
      cursor: 'inherit',
    },
  },

  '& .ProseMirror-selectednode .placeholder, & .ProseMirror-selectednode .placeholder-html': {
    backgroundColor: `${theme.primary}35`,
    borderColor: theme.primaryDark,
    outline: `2px solid ${theme.primary}`,
    outlineOffset: '1px',
  },
})

/**
 * Styles for the placeholder menu button container
 */
export const getPlaceholderMenuButtonStyles = (): SxProps<Theme> => ({
  display: 'inline-flex',
  alignItems: 'center',
})

/**
 * Styles for the placeholder menu button
 */
export const getPlaceholderButtonStyles = (theme: ThemeInterface): SxProps<Theme> => ({
  minWidth: 'auto',
  padding: '4px 8px',
  borderRadius: theme.borderRadius,
  transition: 'background-color 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: `${theme.primary}08`, // 8% opacity
  },
})

/**
 * Styles for menu items in the placeholder dropdown
 */
export const getPlaceholderMenuItemStyles = (): SxProps<Theme> => ({
  fontSize: '14px',
  minHeight: '36px',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
  },
})
